import { FunctionComponent, useEffect, useCallback, useRef } from 'react'
import { useAtom } from 'jotai'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { TooltipOrBottomSheet } from '../primitives/TooltipOrBottomSheet/TooltipOrBottomSheet'
import { useCalendarData } from 'src/hooks/useCalendarData'
import { Text } from '../primitives/Text/Text'
import { Button } from '../primitives/Button/Button'
import { BaseCarousel } from '../primitives/BaseCarousel/BaseCarousel'
import { CalendarDateItem } from './CalendarModal.types'
import { DateItem } from './DateItem/DateItem'
import { calendarSelectorPopover } from 'src/atoms/modals/calendarSelectorPopover'
import { AddressSection } from '../AddressSection/AddressSection'
import { SkipDeliverySection } from '../SkipDeliverySection/SkipDeliverySection'
import { getMetadata } from 'src/redux-api-bridge/metadata/selectors'
import { CoreUxOrderStatus } from '../types/coreUxOrder.types'
import { selectSchedulePanelIsOpen } from 'src/redux-api-bridge/schedule/selectors'
import { extractDateFromNavigationActions } from '../CalendarSelector/utils'
import { useHomeScreenRefetch } from 'src/hooks/useHomeScreenRefetch'
import './CalendarModal.scss'

interface CalendarModalProps {
  date?: string
  parentRef?: React.RefObject<HTMLElement>
}

export const CalendarModal: FunctionComponent<CalendarModalProps> = ({
  date,
  parentRef
}) => {
  const [{ isOpen }, setIsOpen] = useAtom(calendarSelectorPopover)
  const { calendarData, isLoading } = useCalendarData(isOpen, date)
  const isSchedulingPanelOpen = useSelector(selectSchedulePanelIsOpen)
  const metadata = useSelector(getMetadata)
  const isOrderSkipped = metadata.orderStatus === CoreUxOrderStatus.Skipped
  const location = useLocation()
  const { triggerRefetch } = useHomeScreenRefetch()
  const locationCache = useRef<{ pathname: string; search: string } | null>(
    null
  )

  const onClose = useCallback(() => {
    if (!isSchedulingPanelOpen) {
      setIsOpen({ isOpen: false, date })
      locationCache.current = null
    }
  }, [isSchedulingPanelOpen, setIsOpen, date])

  const handleDateSelect = useCallback(
    (dateItem: CalendarDateItem) => {
      const extractedDate = extractDateFromNavigationActions(
        dateItem.actions.onClick
      )
      if (extractedDate) {
        triggerRefetch()
      }
    },
    [triggerRefetch]
  )

  useEffect(() => {
    if (isOpen) {
      if (!locationCache.current) {
        locationCache.current = {
          pathname: location.pathname,
          search: location.search
        }
      } else if (
        locationCache.current.pathname !== location.pathname ||
        locationCache.current.search !== location.search
      ) {
        onClose()
      }
    }
  }, [location, onClose, isOpen])

  // Add cleanup effect for unmounting
  useEffect(() => {
    return () => {
      if (isOpen) {
        setIsOpen({ isOpen: false, date })
      }
    }
  }, [isOpen, setIsOpen, date])

  if (isLoading || !calendarData || !date) {
    return (
      <TooltipOrBottomSheet
        isOpen={isOpen}
        onClose={onClose}
        parentRef={parentRef}>
        <div className="core-ux-calendar-modal">
          <div className="core-ux-calendar-modal__loading-header" />
          <div className="core-ux-calendar-modal__loading-dates" />
          <div className="core-ux-calendar-modal__loading-address" />
        </div>
      </TooltipOrBottomSheet>
    )
  }

  return (
    <TooltipOrBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      parentRef={parentRef}>
      {calendarData && (
        <div className="core-ux-calendar-modal">
          <div className="core-ux-calendar-modal__header">
            <div className="core-ux-calendar-modal__title">
              {calendarData.title && <Text {...calendarData.title} />}
            </div>
            {calendarData.addDeliveryDayButton && (
              <Button
                {...calendarData.addDeliveryDayButton}
                onClick={onClose}
                className="core-ux-calendar-modal__add-delivery-day-button"
              />
            )}
          </div>

          <div className="core-ux-calendar-modal__dates-container">
            <BaseCarousel
              enableMousewheel={true}
              items={(calendarData?.dates ?? []).map(
                (dateItem: CalendarDateItem, index: number) => (
                  <DateItem
                    key={index}
                    day={dateItem.day}
                    dayNumber={dateItem.dayNumber}
                    month={dateItem.month}
                    isSelected={dateItem.isSelected}
                    borderColor={dateItem.borderColor}
                    outlineColor={dateItem.outlineColor}
                    actions={dateItem.actions}
                    onDateSelect={() => handleDateSelect(dateItem)}
                  />
                )
              )}
              swiperProps={{
                spaceBetween: 12,
                slidesPerView: 'auto',
                freeMode: true,
                touchStartPreventDefault: false,
                touchStartForcePreventDefault: false,
                touchRatio: 1,
                touchAngle: 45,
                touchMoveStopPropagation: false,
                threshold: 5,
                longSwipes: false,
                longSwipesRatio: 0.5,
                longSwipesMs: 300,
                shortSwipes: true
              }}
            />
          </div>

          {(isOrderSkipped || calendarData.addressSection) && (
            <div className="core-ux-calendar-modal__divider" />
          )}
          {isOrderSkipped ? (
            <SkipDeliverySection deliveryDate={date} />
          ) : (
            calendarData.addressSection && (
              <AddressSection {...calendarData.addressSection} />
            )
          )}
        </div>
      )}
    </TooltipOrBottomSheet>
  )
}
